import type {
  ThemeBottomSectionImageItem,
  ThemeBottomSectionState,
} from "@/models/ITrackingPageOptions"
import stores from "@/stores"

import { mergeThemeBottomSection } from "./themeBottomSectionDefaults"

/** 顶部 Columns 列表中 Order lookup 行的稳定 id（与 API columns 顺序无关，仅用于编辑器与草稿） */
export const SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID = "showcase-top-order-lookup"

const distributeColSpansEvenly = (
  ids: string[],
  totalCols: number,
  colSpanById: Map<string, number>,
): void => {
  if (ids.length === 0) {
    return
  }
  const per = Math.floor(totalCols / ids.length)
  let leftover = totalCols - per * ids.length

  for (const id of ids) {
    const extra = leftover > 0 ? 1 : 0
    colSpanById.set(id, per + extra)
    leftover -= extra
  }
}

/** 合并持久化顺序与当前图片列表：保留已知顺序，补齐 lookup 与新增图片 */
export const reconcileTopColumnOrder = (
  order: string[],
  images: ThemeBottomSectionImageItem[],
): string[] => {
  const lookupId = SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID
  const imageIds = new Set(images.map((i) => i.id))
  const seen = new Set<string>()
  const result: string[] = []

  for (const id of order) {
    if (id === lookupId && !seen.has(lookupId)) {
      result.push(lookupId)
      seen.add(lookupId)
    } else if (imageIds.has(id) && !seen.has(id)) {
      result.push(id)
      seen.add(id)
    }
  }

  if (!seen.has(lookupId)) {
    result.unshift(lookupId)
    seen.add(lookupId)
  }

  for (const img of images) {
    if (!seen.has(img.id)) {
      result.push(img.id)
    }
  }

  return result
}

export const buildDefaultTopColumnOrder = (
  images: ThemeBottomSectionImageItem[],
): string[] => reconcileTopColumnOrder([SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID], images)

/**
 * 用于 Width 下拉规则的有效列数：**勾选启用**的列（非槽位总数）。
 * Order lookup 固定计 1；每张图片仅在 `enabled !== false` 时计入。上限 3。
 */
export const getShowcaseTopColumnCount = (
  topSectionImages: ThemeBottomSectionImageItem[],
): number => {
  const enabledImageCount = topSectionImages.filter((i) => i.enabled !== false).length

  return Math.min(3, 1 + enabledImageCount)
}

export type ShowcaseOrderLookupWidthPercent = "33" | "50" | "67" | "100"

export interface ThemeLayoutShowcaseContentDraft {
  top_section_images: ThemeBottomSectionImageItem[]
  /** 顶部列在编辑器与预览中的从左到右顺序（含 lookup 行 id） */
  top_section_column_order: string[]
  bottom_section: ThemeBottomSectionState
  width: ShowcaseOrderLookupWidthPercent
  corner_radius: "SHARP" | "STANDARD" | "ROUNDED"
}

export const createInitialShowcaseDraft = (): ThemeLayoutShowcaseContentDraft => {
  const { displayOption } = stores.trackingPageStore

  return {
    top_section_images: [],
    top_section_column_order: [SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID],
    bottom_section: mergeThemeBottomSection(displayOption?.themeBottomSection),
    width: "100",
    corner_radius: "STANDARD",
  }
}

/** 有效列数变化时，将宽度约束到当前可选集合内 */
export const clampShowcaseWidthToColumnCount = (
  columnCount: number,
  current: ShowcaseOrderLookupWidthPercent,
): ShowcaseOrderLookupWidthPercent => {
  if (columnCount >= 3) {
    return "33"
  }
  if (columnCount === 2) {
    if (current === "33" || current === "50" || current === "67") {
      return current
    }
    return "33"
  }
  return "100"
}

/** 12 栅格：Order lookup 宽度下拉值 → 列数 */
export const showcaseOrderLookupPercentToCols = (
  percent: ShowcaseOrderLookupWidthPercent,
): number => {
  if (percent === "33") {
    return 4
  }
  if (percent === "50") {
    return 6
  }
  if (percent === "67") {
    return 8
  }
  return 12
}

/**
 * 首行各块占列数：[查询框, 图1?, 图2?]；与左侧「启用列数 + Width 下拉」一致。
 * - 仅查询：整行 12
 * - 查询 + 1 图：图占剩余列（实现 1/3+2/3、1/2+1/2 等组合）
 * - 查询 + 2 图：两图平分剩余列（各约 1/3 全宽）
 */
export const getShowcaseHeroRowColSpans = (params: {
  enabledTopImageSlots: number
  orderLookupWidthPercent: ShowcaseOrderLookupWidthPercent
}): number[] => {
  const q = showcaseOrderLookupPercentToCols(params.orderLookupWidthPercent)
  const imageSlots = Math.min(2, Math.max(0, params.enabledTopImageSlots))

  if (imageSlots === 0) {
    return [12]
  }

  const rest = Math.max(1, 12 - q)

  if (imageSlots === 1) {
    return [q, rest]
  }

  const halfA = Math.floor(rest / 2)
  const halfB = rest - halfA

  return [q, halfA, halfB]
}

/** 按列顺序与 Width 下拉计算各块在 12 栅格中的 col-span（预览与保存列顺序一致） */
export const getShowcaseHeroLayoutFromOrder = (params: {
  columnOrder: string[]
  images: ThemeBottomSectionImageItem[]
  orderLookupWidthPercent: ShowcaseOrderLookupWidthPercent
}): { orderedEnabledIds: string[]; colSpanById: Map<string, number> } => {
  const lookupId = SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID
  const order = reconcileTopColumnOrder(params.columnOrder, params.images)
  const orderedEnabledIds = order.filter((id) => {
    if (id === lookupId) {
      return true
    }
    const item = params.images.find((i) => i.id === id)

    return item !== undefined && item.enabled !== false
  })
  const colSpanById = new Map<string, number>()
  const imageIds = orderedEnabledIds.filter((id) => id !== lookupId)
  const hasLookup = orderedEnabledIds.includes(lookupId)

  if (orderedEnabledIds.length === 0) {
    return { orderedEnabledIds, colSpanById }
  }

  if (!hasLookup) {
    distributeColSpansEvenly(imageIds, 12, colSpanById)

    return { orderedEnabledIds, colSpanById }
  }

  const lookupCols = showcaseOrderLookupPercentToCols(params.orderLookupWidthPercent)

  if (imageIds.length === 0) {
    colSpanById.set(lookupId, 12)

    return { orderedEnabledIds, colSpanById }
  }

  const rest = Math.max(1, 12 - lookupCols)

  distributeColSpansEvenly(imageIds, rest, colSpanById)
  colSpanById.set(lookupId, lookupCols)

  return { orderedEnabledIds, colSpanById }
}
